import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SeasonalThemeProvider } from './contexts/SeasonalThemeContext';
import SeasonalDecorations from './components/SeasonalDecorations';
import MainLayout from './layouts/MainLayout';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import PractitionerProfile from './pages/PractitionerProfile';
import LabWorkList from './pages/LabWorkList';
import Accounting from './pages/Accounting';
import Settings from './pages/Settings';
import NewJob from './pages/NewJob';
import CaseDossier from './pages/CaseDossier';
import InvoicePage from './pages/InvoicePage';
import Products from './pages/Products';
import ClientDetails from './pages/ClientDetails';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TransferPage from './pages/TransferPage';
import ProtectedRoute from './components/ProtectedRoute';
import './styles/App.css';
import ClientDashboard from './pages/ClientDashboard';
import MyProfile from './pages/MyProfile';

// Component to handle role-based redirects
function RoleBasedRedirect() {
  const { profile, loading } = useAuth();

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Chargement...</div>;
  }

  // Redirect based on role
  if (profile?.role === 'admin') {
    return <Navigate to="/dashboard" replace />;
  } else if (profile?.role === 'producteur') {
    return <Navigate to="/jobs" replace />;
  } else if (profile?.role === 'client') {
    return <Navigate to="/client-dashboard" replace />;
  }

  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <LanguageProvider>
          <SeasonalThemeProvider>
            <SeasonalDecorations />
            <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/transfert/:token" element={<TransferPage />} />

            {/* Root redirect based on role */}
            <Route path="/" element={<RoleBasedRedirect />} />

            {/* Protected admin-only routes wrapped in MainLayout */}
            <Route element={
              <ProtectedRoute requireAdmin={true}>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/jobs/new" element={<NewJob />} />
              <Route path="/jobs/edit/:orderId" element={<NewJob />} />
              <Route path="/products" element={<Products />} />
              <Route path="/invoices/new/:orderId" element={<InvoicePage />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:clientId" element={<ClientDetails />} />
              <Route path="/accounting" element={<Accounting />} />
              <Route path="/settings" element={<Settings />} />
            </Route>

            {/* Shared routes: admin + producteur (wrapped in MainLayout) */}
            <Route element={
              <ProtectedRoute allowedRoles={['admin', 'producteur']}>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route path="/jobs" element={<LabWorkList />} />
              <Route path="/jobs/:orderId" element={<CaseDossier />} />
            </Route>

            {/* Client dashboard route */}
            <Route path="/client-dashboard" element={
              <ProtectedRoute>
                <ClientDashboard />
              </ProtectedRoute>
            } />

            {/* Profile route - accessible to all authenticated users */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route index element={<MyProfile />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
          </SeasonalThemeProvider>
        </LanguageProvider>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;

// Trigger rebuild with env vars 

